import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { useLoading } from '@/contexts/LoadingContext';

interface InitializationPhase {
  name: string;
  progress: number;
  message: string;
  duration: number;
}

const INITIALIZATION_PHASES: InitializationPhase[] = [
  { name: 'auth', progress: 20, message: 'Authenticating...', duration: 800 },
  { name: 'profile', progress: 40, message: 'Loading profile...', duration: 600 },
  { name: 'routes', progress: 60, message: 'Preparing routes...', duration: 400 },
  { name: 'mount', progress: 80, message: 'Mounting components...', duration: 300 },
  { name: 'ready', progress: 100, message: 'Ready!', duration: 200 }
];

export const useAppInitialization = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const { user, isLoading } = useAuth();
  const { setLoading, clearLoading } = useLoading();

  const getCurrentPhase = useCallback(() => {
    return INITIALIZATION_PHASES[currentPhase] || INITIALIZATION_PHASES[0];
  }, [currentPhase]);

  const advancePhase = useCallback(() => {
    if (currentPhase < INITIALIZATION_PHASES.length - 1) {
      setCurrentPhase(prev => prev + 1);
    } else {
      setIsComplete(true);
      clearLoading('app-initialization');
    }
  }, [currentPhase, clearLoading]);

  // Initialize app loading
  useEffect(() => {
    setLoading('app-initialization', true, 'Starting PrayAI...', 'general');
    
    return () => {
      clearLoading('app-initialization');
    };
  }, [setLoading, clearLoading]);

  // Progress through phases based on auth state
  useEffect(() => {
    if (isComplete) return;

    const phase = getCurrentPhase();
    setProgress(phase.progress);
    
    // Auto-advance through phases with realistic timing
    const timer = setTimeout(() => {
      // Skip profile phase if not loading
      if (phase.name === 'profile' && !isLoading) {
        advancePhase();
        return;
      }
      
      // Skip auth phase if user is already loaded
      if (phase.name === 'auth' && user !== undefined) {
        advancePhase();
        return;
      }
      
      advancePhase();
    }, phase.duration);

    return () => clearTimeout(timer);
  }, [currentPhase, isComplete, user, isLoading, getCurrentPhase, advancePhase]);

  // Update loading message as phases progress
  useEffect(() => {
    if (!isComplete) {
      const phase = getCurrentPhase();
      setLoading('app-initialization', true, phase.message, 'general');
    }
  }, [currentPhase, isComplete, setLoading, getCurrentPhase]);

  return {
    progress,
    currentPhase: getCurrentPhase(),
    isComplete,
    phases: INITIALIZATION_PHASES
  };
};